import React, { useEffect, useCallback } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list';
import { useSurveyStore } from '../../stores/surveyStore';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import type { SurveyView } from './types';
import SurveyBoard from './SurveyBoard';
import SurveyEditor from './SurveyEditor';
import SurveyResults from './SurveyResults';

const SurveyPortal: React.FC = () => {
  const { view, setView, activeSurvey, reset } = useSurveyStore();
  const { surveyPortalOpen, setSurveyPortalOpen, language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const handleClose = useCallback(() => {
    setSurveyPortalOpen(false);
    reset();
  }, [setSurveyPortalOpen, reset]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (surveyPortalOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [surveyPortalOpen, handleClose]);

  if (!surveyPortalOpen) return null;

  const tabs: { key: SurveyView; label: string }[] = [
    { key: 'board', label: t('surveyTabBoard') },
  ];
  if (view === 'editor' || activeSurvey?.status === 'draft') {
    tabs.push({ key: 'editor', label: t('surveyTabEditor') });
  }
  if (activeSurvey?.status === 'completed') {
    tabs.push({ key: 'results', label: t('surveyTabResults') });
  }

  return (
    <div className="fixed inset-0 z-overlay flex flex-col bg-surface-page animate-fade-in-down">
      <header className="flex items-center justify-between px-6 py-4 bg-surface-page border-b border-subtle">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10">
            <ClipboardList size={20} className="text-primary" />
          </div>
          <h1 className="text-lg font-semibold text-content tracking-tight">{t('surveyPortalTitle')}</h1>
        </div>

        <nav className="flex items-center gap-1 bg-surface-primary rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setView(tab.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                view === tab.key
                  ? 'bg-brand/15 text-primary shadow-sm'
                  : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <button
          onClick={handleClose}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-content-muted hover:text-content hover:bg-surface-secondary transition-all duration-200"
        >
          <X size={20} />
        </button>
      </header>

      <main className="flex-1 overflow-hidden">
        {view === 'board' && <SurveyBoard />}
        {view === 'editor' && <SurveyEditor />}
        {view === 'results' && <SurveyResults />}
      </main>
    </div>
  );
};

export default React.memo(SurveyPortal);
